import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { isAxiosError } from 'axios';
import { Link } from 'react-router-dom';
import { typedResolver } from '@/lib/typedResolver';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@marine/ui/dialog';
import { Button } from '@marine/ui/button';
import { Input } from '@marine/ui/input';
import { Label } from '@marine/ui/label';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@marine/ui/collapsible';
import { toast } from 'sonner';
import { getErrorCode, getErrorMessage } from '@marine/utils/getErrorMessage';
import { useCreateCustomerBoat, useCustomerBoats } from '../hooks/useCustomers';
import type { CreateCustomerBoatRequest } from '../services/customerApi';
import type { CustomerBoat } from '../types/crm.types';

// D9-12 (#408) — a CRM component reads its own 409 `details`, rather than
// importing Service's `serviceErrorMessage`/`applyFieldErrors` (09-03): that
// util's switch is keyed to the Service door vocabulary
// (WORK_ORDER_NOT_EDITABLE, INVOICE_EMPTY, ...) and, more importantly, never
// surfaces the `details` object this dialog needs to build the T-09-03
// conflict link. `getErrorCode`/`getErrorMessage` from `@marine/utils` are
// genuinely generic and are reused; the 409/422 mapping itself is local.

const CURRENT_YEAR = new Date().getFullYear();

const optionalNumber = (schema: z.ZodNumber) =>
  z.preprocess((value) => {
    if (value === '' || value === null || value === undefined) return undefined;
    if (typeof value === 'number' && Number.isNaN(value)) return undefined;
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }, schema.optional());

const customerBoatSchema = z.object({
  year: z.number().int().min(1900, 'Year must be 1900 or later').max(CURRENT_YEAR + 1, `Year must be ${CURRENT_YEAR + 1} or earlier`),
  manufacturer: z.string().min(1, 'Manufacturer is required').max(255),
  model: z.string().min(1, 'Model is required').max(255),
  boat_type: z.string().min(1, 'Boat type is required').max(255),
  name: z.string().max(255).optional(),
  // Optional at entry — no format check (older and foreign HIN formats exist, D9-12).
  hin: z.string().max(20).optional(),
  registration_number: z.string().max(255).optional(),
  engine_make: z.string().max(255).optional(),
  engine_model: z.string().max(255).optional(),
  // 09-REVIEW WR-07 — bounds mirror Create/UpdateCustomerBoatRequest (both
  // columns are Postgres `integer`; an unbounded value used to 500 on insert).
  engine_year: optionalNumber(
    z
      .number()
      .int()
      .min(1900, 'Engine year must be 1900 or later')
      .max(CURRENT_YEAR + 1, `Engine year must be ${CURRENT_YEAR + 1} or earlier`),
  ),
  engine_serial: z.string().max(255).optional(),
  engine_hours: optionalNumber(z.number().int().min(0).max(1000000, 'Engine hours must be 1,000,000 or fewer')),
});

type CustomerBoatFormData = z.infer<typeof customerBoatSchema>;

/**
 * 09-UAT gap 2 — the allowlist of field names this dialog actually renders,
 * derived from the schema itself so it can never drift from the form: a
 * field added to `customerBoatSchema` is automatically "known". A 422 naming
 * any OTHER key (e.g. `location_id`, which has no input on this form) is
 * routed to the root error / toast below instead of an invisible
 * `form.setError` on a field nobody renders.
 */
const FORM_FIELD_NAMES: readonly string[] = Object.keys(customerBoatSchema.shape);

const customerBoatDefaults: CustomerBoatFormData = {
  year: CURRENT_YEAR,
  manufacturer: '',
  model: '',
  boat_type: '',
  name: '',
  hin: '',
  registration_number: '',
  engine_make: '',
  engine_model: '',
  engine_year: undefined,
  engine_serial: '',
  engine_hours: undefined,
};

/**
 * Builds the request body from validated form values, OMITTING every blank
 * optional field entirely rather than sending it as an empty string — a
 * HIN-less walk-in entry must issue a request with no `hin` key at all.
 */
function buildPayload(values: CustomerBoatFormData): CreateCustomerBoatRequest {
  const payload: CreateCustomerBoatRequest = {
    year: values.year,
    manufacturer: values.manufacturer,
    model: values.model,
    boat_type: values.boat_type,
  };

  if (values.name) payload.name = values.name;
  if (values.hin) payload.hin = values.hin;
  if (values.registration_number) payload.registration_number = values.registration_number;
  if (values.engine_make) payload.engine_make = values.engine_make;
  if (values.engine_model) payload.engine_model = values.engine_model;
  if (values.engine_year !== undefined) payload.engine_year = values.engine_year;
  if (values.engine_serial) payload.engine_serial = values.engine_serial;
  if (values.engine_hours !== undefined) payload.engine_hours = values.engine_hours;

  return payload;
}

/** T-09-03 — only rendered when the server's `details` names a record the caller may view. */
interface HinConflictLink {
  href: string;
  label: string;
}

function extractDetails(error: unknown): Record<string, unknown> | undefined {
  if (!isAxiosError(error)) return undefined;
  const details = (error.response?.data as { details?: unknown } | undefined)?.details;
  return details && typeof details === 'object' ? (details as Record<string, unknown>) : undefined;
}

function extractFieldErrors(error: unknown): Record<string, string[]> | undefined {
  if (!isAxiosError(error)) return undefined;
  const errors = (error.response?.data as { errors?: unknown } | undefined)?.errors;
  return errors && typeof errors === 'object' ? (errors as Record<string, string[]>) : undefined;
}

function buildHinConflictLink(details: Record<string, unknown> | undefined): HinConflictLink | null {
  if (!details) return null;

  if (typeof details.customer_id === 'string') {
    return { href: `/crm/customers/${details.customer_id}`, label: 'View existing customer' };
  }

  if (typeof details.inventory_boat_id === 'string') {
    return { href: `/inventory/boats/${details.inventory_boat_id}`, label: 'View inventory boat' };
  }

  return null;
}

function boatLabel(boat: CustomerBoat): string {
  return [boat.year, boat.manufacturer, boat.model].filter(Boolean).join(' ') || boat.name || 'Boat';
}

interface CustomerBoatQuickCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  customerName?: string;
  onSuccess?: (boatId: string) => void;
}

export function CustomerBoatQuickCreateDialog({
  open,
  onOpenChange,
  customerId,
  customerName,
  onSuccess,
}: CustomerBoatQuickCreateDialogProps) {
  const [showForm, setShowForm] = useState(false);
  const [hinConflictLink, setHinConflictLink] = useState<HinConflictLink | null>(null);
  const { data: existingBoatsData } = useCustomerBoats(customerId);
  const createBoat = useCreateCustomerBoat(customerId);

  const existingBoats = existingBoatsData?.items ?? [];
  const hasExistingBoats = existingBoats.length > 0;
  const formVisible = !hasExistingBoats || showForm;

  const form = useForm<CustomerBoatFormData>({
    resolver: typedResolver(customerBoatSchema),
    defaultValues: customerBoatDefaults,
  });

  useEffect(() => {
    if (!open) return;
    form.reset(customerBoatDefaults);
    setShowForm(false);
    setHinConflictLink(null);
  }, [open, form]);

  const onSubmit = async (values: CustomerBoatFormData) => {
    setHinConflictLink(null);
    // 09-UAT gap 2 — clear a stale off-form refusal so a retry doesn't keep
    // showing a reason the server may no longer be refusing on.
    form.clearErrors('root.serverError');

    try {
      const created = await createBoat.mutateAsync(buildPayload(values));
      toast.success('Boat added');
      onOpenChange(false);
      form.reset(customerBoatDefaults);
      setShowForm(false);

      // The 100ms delay lets the invalidated customerKeys.boats(customerId)
      // query refetch before the caller's picker looks for the new record —
      // reproduced verbatim from QuickCreateCustomerDialog.tsx:74-78.
      if (onSuccess && created?.id) {
        setTimeout(() => {
          onSuccess(created.id);
        }, 100);
      }
    } catch (error: unknown) {
      const code = getErrorCode(error);

      if (code === 'HIN_ALREADY_ON_FILE') {
        setHinConflictLink(buildHinConflictLink(extractDetails(error)));
        form.setError('hin', {
          type: 'server',
          message: getErrorMessage(error, 'That HIN is already on file.'),
        });
        return;
      }

      if (code === 'VALIDATION_ERROR') {
        const fieldErrors = extractFieldErrors(error);

        if (fieldErrors) {
          // 09-UAT gap 2 — a field the server names that this form does not
          // render (e.g. `location_id`, CR-03) used to set an invisible RHF
          // error and silently swallow the request: `form.setError` alone,
          // then an early `return` that skipped the generic toast below.
          // Partition on the schema-derived allowlist so an off-form refusal
          // is always surfaced, on-form behaviour is unchanged.
          const offFormMessages: string[] = [];

          for (const [field, messages] of Object.entries(fieldErrors)) {
            const [first] = messages;
            if (!first) continue;

            if (FORM_FIELD_NAMES.includes(field)) {
              form.setError(field as keyof CustomerBoatFormData, { type: 'server', message: first });
            } else {
              offFormMessages.push(first);
            }
          }

          if (offFormMessages.length > 0) {
            const message = offFormMessages.join(' ');
            // The server's own message names the reason — pass it through
            // verbatim rather than inventing replacement copy, the same
            // precedent HIN_ALREADY_ON_FILE follows above.
            form.setError('root.serverError', { type: 'server', message });
            toast.error(message);
          }

          return;
        }

        // No `errors` object at all — fall through to the generic toast
        // instead of returning silently.
      }

      toast.error(getErrorMessage(error, 'Failed to add boat.'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Add Boat</DialogTitle>
          <DialogDescription>
            {customerName ? `Add a boat for ${customerName}.` : 'Add a boat for this customer.'}
          </DialogDescription>
        </DialogHeader>

        {hasExistingBoats && (
          <div className="space-y-2 rounded-md border border-border p-3">
            <p className="text-sm font-medium">
              Already on file for {customerName ?? 'this customer'} — is it one of these?
            </p>
            <div className="space-y-1.5">
              {existingBoats.map((boat) => (
                <div
                  key={boat.id}
                  className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
                >
                  <div className="text-sm">
                    <span className="font-medium">{boatLabel(boat)}</span>{' '}
                    <span className="text-muted-foreground">
                      {boat.hin ? `HIN: ${boat.hin}` : 'HIN not recorded'}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="text-xs font-medium text-link hover:underline shrink-0"
                    onClick={() => {
                      onSuccess?.(boat.id);
                      onOpenChange(false);
                    }}
                  >
                    Use this boat
                  </button>
                </div>
              ))}
            </div>
            {!showForm && (
              <button
                type="button"
                className="text-sm text-link hover:underline"
                onClick={() => setShowForm(true)}
              >
                None of these — add a new boat
              </button>
            )}
          </div>
        )}

        {formVisible && (
          <form
            onSubmit={(event) => {
              // 09-UAT gap 1 (defence in depth) — a submit event bubbles
              // through a Radix Dialog portal to any ancestor <form>. This
              // dialog is never meant to be mounted inside another form, but
              // stop propagation anyway so a future misuse can't resurrect
              // the double-submit bug.
              event.stopPropagation();
              void form.handleSubmit(onSubmit)(event);
            }}
            className="space-y-4"
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="year">
                  Year <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="year"
                  type="number"
                  {...form.register('year', { valueAsNumber: true })}
                />
                {form.formState.errors.year && (
                  <p className="text-xs text-destructive">{form.formState.errors.year.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="manufacturer">
                  Manufacturer <span className="text-destructive">*</span>
                </Label>
                <Input id="manufacturer" {...form.register('manufacturer')} autoFocus />
                {form.formState.errors.manufacturer && (
                  <p className="text-xs text-destructive">{form.formState.errors.manufacturer.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="model">
                  Model <span className="text-destructive">*</span>
                </Label>
                <Input id="model" {...form.register('model')} />
                {form.formState.errors.model && (
                  <p className="text-xs text-destructive">{form.formState.errors.model.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="boat_type">
                  Boat type <span className="text-destructive">*</span>
                </Label>
                <Input id="boat_type" {...form.register('boat_type')} placeholder="e.g. Center Console" />
                {form.formState.errors.boat_type && (
                  <p className="text-xs text-destructive">{form.formState.errors.boat_type.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Boat name</Label>
                <Input id="name" {...form.register('name')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hin">HIN</Label>
                <Input
                  id="hin"
                  {...form.register('hin')}
                  aria-describedby="hin-helper-text hin-error-text"
                  aria-invalid={!!form.formState.errors.hin}
                />
                <p id="hin-helper-text" className="text-xs text-muted-foreground">
                  Leave blank if unknown — add it once you've seen the boat
                </p>
                {form.formState.errors.hin && (
                  <p id="hin-error-text" className="text-xs text-destructive">
                    {form.formState.errors.hin.message}
                    {hinConflictLink && (
                      <>
                        {' '}
                        <Link to={hinConflictLink.href} className="underline">
                          {hinConflictLink.label}
                        </Link>
                      </>
                    )}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="registration_number">Registration #</Label>
              <Input id="registration_number" {...form.register('registration_number')} />
            </div>

            <Collapsible>
              <CollapsibleTrigger asChild>
                <Button type="button" variant="ghost" size="sm" className="px-0">
                  Engine
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4 pt-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="engine_make">Engine make</Label>
                    <Input id="engine_make" {...form.register('engine_make')} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="engine_model">Engine model</Label>
                    <Input id="engine_model" {...form.register('engine_model')} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="engine_year">Engine year</Label>
                    <Input
                      id="engine_year"
                      type="number"
                      aria-invalid={!!form.formState.errors.engine_year}
                      {...form.register('engine_year', { valueAsNumber: true })}
                    />
                    {form.formState.errors.engine_year && (
                      <p className="text-xs text-destructive">{form.formState.errors.engine_year.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="engine_serial">Engine serial</Label>
                    <Input id="engine_serial" {...form.register('engine_serial')} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="engine_hours">Engine hours</Label>
                  <Input
                    id="engine_hours"
                    type="number"
                    aria-invalid={!!form.formState.errors.engine_hours}
                    {...form.register('engine_hours', { valueAsNumber: true })}
                  />
                  {form.formState.errors.engine_hours && (
                    <p className="text-xs text-destructive">{form.formState.errors.engine_hours.message}</p>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* 09-UAT gap 2 — a refusal naming a field this form does not
                render (e.g. `location_id`) has nowhere else to show; the
                server's own message renders here verbatim. */}
            {form.formState.errors.root?.serverError?.message && (
              <p role="alert" className="text-xs text-destructive">
                {form.formState.errors.root.serverError.message}
              </p>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={createBoat.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createBoat.isPending}>
                {createBoat.isPending ? 'Adding...' : 'Add boat'}
              </Button>
            </DialogFooter>
          </form>
        )}

        {!formVisible && (
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
